import React, { useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DialogFrame } from "component/shared/dialog-frame";
import { FormField } from "component/shared/form-fields";
import { OutlinedButton } from "component/shared/outlined-button";
import { Switch } from "component/shared/switch";
import { Tag } from "component/shared/tag";
import { Page } from "model/page";
import { WebsitePlatformType } from "type/website-platform";
import { PageEmbedCode } from "../../page-setup";
import {
	bareUrl,
	defaultSlug,
	displaySlug,
	hasQueryVariables,
	isRecommended,
	isSettingUp,
	isStatusPage,
	pageLabel,
	pathToSave,
	statusPageUrl,
	withPageNoun,
} from "../page-catalog";
import { VariableField } from "../variable-field";
import { ModalValues, initialValues, isDirty, pageToSave, primaryButton, resetVisible, slugProblem, urlWarningVisible } from "./modal-state";
import { styles } from "./style";

const COPY = {
	pageLocation: "Where this IDX page lives on your website.",
	urlWarning:
		"Changing this doesn't change your website. Make sure your page is at the new address, or IDX links will stop working.",
	customSlugOff: "When off, visitors access this content at",
	customSlugEnable: "Enable this setting to use a custom slug for this page.",
	slugInstruction: "You can edit your custom slug below (part of the web address following your domain).",
	slugRecommend: "We recommend using",
	createPageAt: "Create a page at:",
	queryVariablesCaution: "Caution: This page requires variables to load the IDX content.",
	resetConfirm: "Reset the URL Path back to factory default?\nYou will still need to save for the changes to take effect.",
	metadata: "Sets the browser tab title and head tags for this page.",
	metadataVariables: "Each variable is replaced with real information when the page loads. If you remove one, nothing takes its place.",
	resetToDefault: "Reset to default",
	discardTitle: "Discard unsaved changes?",
	discardBody: "Your changes to this page haven't been saved.",
	saveError: "Couldn't save this page. Try again, or contact support if it keeps happening.",
};

const NO_VALUES: ModalValues = { slug: "", enabled: false, title: "", head: "" };

interface OwnProps {
	/** The page to set up or edit, or undefined when the modal is closed. */
	page?: Page;
	baseUrl: string;
	/** The Default IDX Page's path, which a page's links go through while it isn't enabled. */
	statusPagePath: string;
	platform: WebsitePlatformType;
	/** Persists the page; reject to keep the modal open with an error. */
	onSave: (page: Page) => Promise<void>;
	onClose: () => void;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

/**
 * The modal for one IDX page (MAX-4419 OSEO 12, 13; MAX-4533 v3). One layout for both jobs: a page
 * that is not set up opens it as **Set Up** (toggle off, as saved; turning it on pre-fills the slug
 * with the type's default and the button reads "Complete Setup"), any other page as **Edit**. Leaving
 * the toggle off while setting up closes without enabling anything: the page stays Disabled, which is
 * what its links already assume.
 * Verify (OSEO 14-17) waits for SPFF-3, and Remove (OSEO 18) is dropped under D3. On the Default IDX
 * Page it edits only the slug and metadata: that page is never switched off. What the buttons and
 * the save do for each mode lives in `modal-state.ts`.
 */
const Component: React.FC<Props> = ({ classes = {}, page, baseUrl, statusPagePath, platform, onSave, onClose }) => {
	// What the modal opened with, and what the user has made of it since.
	const [initial, setInitial] = useState<ModalValues>(NO_VALUES);
	const [values, setValues] = useState<ModalValues>(NO_VALUES);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string>();
	const [confirmDiscard, setConfirmDiscard] = useState(false);
	// The slug shows as the whole address with a pencil; the pencil opens the editor.
	const [editingSlug, setEditingSlug] = useState(false);
	// Whether the user has committed a slug change with Done (or Reset); the URL warning waits for it.
	// It lasts until the modal closes, and a reopened modal starts without it.
	const [committed, setCommitted] = useState(false);

	useEffect(() => {
		if (page) {
			const start = initialValues(page);
			setInitial(start);
			setValues(start);
			setError(undefined);
			setConfirmDiscard(false);
			setCommitted(false);
			// Opens on the editor when the saved path already fails validation, so the error and Reset are in view.
			setEditingSlug(!!slugProblem(page, start));
		}
	}, [page]);

	// Where focus goes when the slug switches between the box and the editor: into the input on the
	// pencil, back to the pencil on Done or Reset, so a keyboard user isn't left on a button that is gone.
	const slugInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
	const pencilRef = useRef<HTMLButtonElement>(null);
	const focusAfterSwitch = useRef<"input" | "pencil">();
	useEffect(() => {
		if (focusAfterSwitch.current) {
			(focusAfterSwitch.current === "input" ? slugInputRef.current : pencilRef.current)?.focus();
			focusAfterSwitch.current = undefined;
		}
	}, [editingSlug]);
	const openSlugEditor = () => {
		focusAfterSwitch.current = "input";
		setEditingSlug(true);
	};
	const closeSlugEditor = () => {
		focusAfterSwitch.current = "pencil";
		setEditingSlug(false);
	};
	// Done and Reset both leave the editor with the slug as it now stands, which commits it.
	const commitSlug = () => {
		setCommitted(true);
		closeSlugEditor();
	};

	if (!page) {
		return null;
	}

	const change = (next: Partial<ModalValues>) => setValues((current) => ({ ...current, ...next }));

	const label = pageLabel(page);
	const settingUp = isSettingUp(page);
	const statusPage = isStatusPage(page);
	// The toggle is `enabled`. Off, the page's links go through the Default IDX Page, so there is no
	// page to build and no embed code to paste; the status page itself is always on.
	const customSlug = statusPage || values.enabled;
	const dirty = isDirty(page, initial, values);
	// With the toggle off, the slug field isn't shown, so a hidden field neither validates nor counts.
	const invalid = slugProblem(page, values);
	// Compared normalised, so a leading or trailing slash alone doesn't count as a change (OSEO 13).
	const recommendedSlug = defaultSlug(page);
	const prefix = (baseUrl || "").replace(/\/+$/, "");
	const variables = page.type.variables || [];
	const button = primaryButton(page, values, { dirty, saving, invalid: !!invalid });
	const defaultPath = page.type.defaultPath;

	const requestClose = () => {
		if (saving) {
			return;
		}
		if (dirty) {
			setConfirmDiscard(true);
		} else {
			onClose();
		}
	};

	const handleSave = async () => {
		if (invalid) {
			return;
		}
		const next = pageToSave(page, values, dirty);
		if (!next) {
			onClose();
			return;
		}
		setSaving(true);
		setError(undefined);
		try {
			await onSave(next);
			onClose();
		} catch(error) {
			setError(COPY.saveError);
		} finally {
			setSaving(false);
		}
	};

	return (
		<DialogFrame
			open
			onClose={requestClose}
			title={`${settingUp ? "Set Up" : "Edit"} ${withPageNoun(label, "Page")}`}
			titleId="edit-page-dialog-title"
			width={840}
			stickyHeader
			notice={error && <MuiAlert severity="error">{error}</MuiAlert>}
			actions={
				<>
					<OutlinedButton onClick={requestClose} disabled={saving}>
						Cancel
					</OutlinedButton>
					<Mui.Button variant="contained" color="primary" onClick={handleSave} disabled={button.disabled}>
						{button.label}
					</Mui.Button>
				</>
			}
		>
			<div className={classes.body}>
				<div className={classes.section}>
					<Mui.Typography className={classes.sectionTitle}>Page Location</Mui.Typography>
					<Mui.Typography className={classes.sectionDescription}>{COPY.pageLocation}</Mui.Typography>
					{!statusPage && (
						<Switch
							checked={values.enabled}
							onChange={(enabled) => change({ enabled })}
							label={
								<span className={classes.toggleLabel}>
									{`Use a custom slug for your ${withPageNoun(label, "page")}`}
									{isRecommended(page) && <Tag label="Recommended" variant="success" size="small" />}
								</span>
							}
							// Off only: on, the slug instructions below take its place (MAX-4577). The address keeps
							// its query variables, written the way ihf-root builds it (`/idx?path=/listing&id={id}`).
							description={
								!values.enabled && (
									<>
										{COPY.customSlugOff} <em>{bareUrl(statusPageUrl(baseUrl, statusPagePath, page.path))}</em>.{" "}
										{COPY.customSlugEnable}
									</>
								)
							}
						/>
					)}
					{customSlug && (
						// Disclosed by the toggle, so it lines up with the toggle's label rather than the toggle itself.
						<div className={statusPage ? classes.disclosedFlush : classes.disclosed}>
							{!statusPage && (
								<Mui.Typography className={classes.instruction}>
									{COPY.slugInstruction}
									{recommendedSlug && (
										<>
											{" "}
											{COPY.slugRecommend} <strong>{recommendedSlug}</strong>.
										</>
									)}
								</Mui.Typography>
							)}
							{/* The old editor's warning, for the types whose path carries the variables the page loads with. */}
							{editingSlug && hasQueryVariables(page) && (
								<Mui.Typography className={classes.instruction}>{COPY.queryVariablesCaution}</Mui.Typography>
							)}
							{!editingSlug && <Mui.Typography className={classes.instruction}>{COPY.createPageAt}</Mui.Typography>}
							{!editingSlug ? (
								<div className={classes.urlBox}>
									{/* Where the page is created: the path without its query variables, as the old editor showed it. */}
									<span className={classes.urlText}>
										{prefix}/<strong>{displaySlug(values.slug).slice(1)}</strong>
									</span>
									<Mui.IconButton ref={pencilRef} className={classes.pencil} size="small" aria-label={statusPage ? "Edit page URL" : "Edit custom slug"} onClick={openSlugEditor}>
										<Icons.Edit size={18} />
									</Mui.IconButton>
								</div>
							) : (
								<div className={classes.slugRow}>
									<span className={classes.slugPrefix}>{prefix}/</span>
									<div className={classes.slugColumn}>
										<div className={classes.slugInput}>
											<FormField
												id="edit-page-url"
												label={statusPage ? "Page URL" : "Custom slug"}
												hideLabel
											inputRef={slugInputRef}
												// The whole path, query variables included; the slash before it is the prefix's.
												value={values.slug.replace(/^\/+/, "")}
												onChange={(event) => change({ slug: pathToSave(event.target.value.replace(/^\/+/, "")) })}
												error={!!invalid}
												helperText={invalid}
												spellCheck={false}
											/>
										</div>
										{defaultPath && resetVisible(page, values, editingSlug) && (
											<div className={classes.resetRow}>
												<Mui.Button
													className={classes.tertiaryButton}
													variant="text"
													onClick={() => {
														// The old editor's confirmation: Reset puts the query variables back too.
														if (window.confirm(COPY.resetConfirm)) {
															change({ slug: defaultPath });
															commitSlug();
														}
													}}
												>
													{COPY.resetToDefault}
												</Mui.Button>
											</div>
										)}
									</div>
									<OutlinedButton className={classes.doneButton} disabled={!!invalid} onClick={commitSlug}>
										Done
									</OutlinedButton>
								</div>
							)}
							{/* Once a changed slug of a live page is committed with Done, and until the modal closes (OSEO 13).
							    Not while the editor is open again: the warning waits for the next Done, not for typing (MAX-4572). */}
							{!editingSlug && urlWarningVisible(page, initial, values, committed) && (
								<div className={classes.warning} role="status">
									<Icons.AlertTriangle size={16} className={classes.warningIcon} />
									<span>{COPY.urlWarning}</span>
								</div>
							)}
						</div>
					)}
				</div>
				<Mui.Divider className={classes.divider} />
				{customSlug && (
					<>
						<div className={classes.section}>
							<Mui.Typography className={classes.sectionTitle}>Embed Code</Mui.Typography>
							<Mui.Typography className={classes.sectionDescription}>
								{`Add this code to your ${withPageNoun(label, "page")}.`}
							</Mui.Typography>
							<PageEmbedCode websitePlatform={platform} />
						</div>
						<Mui.Divider className={classes.divider} />
					</>
				)}
				<div className={classes.section}>
					<Mui.Typography className={classes.sectionTitle}>Page Metadata</Mui.Typography>
					<Mui.Typography className={classes.sectionDescription}>
						{COPY.metadata}
						{variables.length > 0 ? ` ${COPY.metadataVariables}` : ""}
					</Mui.Typography>
					<VariableField
						id="edit-page-title"
						label="Page Title"
						value={values.title}
						onChange={(title) => change({ title })}
						variables={variables}
					/>
					<VariableField
						id="edit-page-head"
						label="Page Head"
						value={values.head}
						onChange={(head) => change({ head })}
						variables={variables}
						multiline
						rows={3}
					/>
				</div>
			</div>
			<DialogFrame
				open={confirmDiscard}
				onClose={() => setConfirmDiscard(false)}
				title={COPY.discardTitle}
				titleId="edit-page-discard-title"
				aria-describedby="edit-page-discard-body"
				actions={
					<>
						<OutlinedButton onClick={() => setConfirmDiscard(false)}>
							Keep Editing
						</OutlinedButton>
						<Mui.Button variant="contained" color="primary" onClick={onClose}>
							Discard
						</Mui.Button>
					</>
				}
			>
				<Mui.Typography id="edit-page-discard-body" className={classes.discardText}>
					{COPY.discardBody}
				</Mui.Typography>
			</DialogFrame>
		</DialogFrame>
	);
};

export const EditPageDialog = withStyles(Component, styles);
